import {
  Carriers,
  isUspsCarrier,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
  DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
  DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Card } from "@redotech/redo-web/card";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { FormMultiSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { FormSwitch } from "@redotech/redo-web/switch";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input, listInput } from "@redotech/ui/form";
import { Validator } from "@redotech/ui/validator";
import { memo, useState } from "react";
import { OutboundLabelForm } from "../outbound-labels";

const beatRateRangeValidator: Validator<{
  minBeatRateBy: number;
  maxBeatRateBy: number;
}> = (value) => {
  if (value.minBeatRateBy > value.maxBeatRateBy) {
    return ["Min beat rate by must not exceed max beat rate by"];
  }
  return [];
};

const carrierSpreadOverrideForm = groupInput(
  {
    carriers: input<Carriers[]>(),
    minBeatRateBy: input<number>(),
    maxBeatRateBy: input<number>(),
  },
  { validator: beatRateRangeValidator },
);

export type CarrierSpreadOverrideValue = InputSchema.Value<
  typeof carrierSpreadOverrideForm
>;

export const carrierSpreadOverrideDefault: CarrierSpreadOverrideValue = {
  carriers: [],
  minBeatRateBy: DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
  maxBeatRateBy: DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
};

const carrierSpreadOverridesList = listInput(
  () => carrierSpreadOverrideForm,
  () => carrierSpreadOverrideDefault,
  (_, index) => index,
);

const defaultBeatRateRangeValidator: Validator<{
  minBeatRateBy: number;
  beatRateBy: number;
}> = (value) => {
  if (value.minBeatRateBy > value.beatRateBy) {
    return ["Min beat rate by must not exceed max beat rate by"];
  }
  return [];
};

export const competitiveSpreadSettingsForm = groupInput(
  {
    enabled: input<boolean>(),
    beatRateBy: input<number>(),
    minBeatRateBy: input<number>(),
    carrierSpreadOverrides: carrierSpreadOverridesList,
    minSpreadFloor: input<number>(),
    enabledCarriers: input<Carriers[]>(),
    applyMerchantCompSpreadWhenTied: input<boolean>(),
  },
  { validator: defaultBeatRateRangeValidator },
);

export const redoCompetitiveSpreadSettingsForm = groupInput(
  {
    enabled: input<boolean>(),
    beatRateBy: input<number>(),
    minBeatRateBy: input<number>(),
    carrierSpreadOverrides: carrierSpreadOverridesList,
    minSpreadDifference: input<number>(),
    enabledCarriers: input<Carriers[]>(),
  },
  { validator: defaultBeatRateRangeValidator },
);

export type CompetitiveSpreadSettingsForm = InputSchema.Input<
  typeof competitiveSpreadSettingsForm
>;

export type CompetitiveSpreadSettingsValue = InputSchema.Value<
  typeof competitiveSpreadSettingsForm
>;

export type RedoCompetitiveSpreadSettingsForm = InputSchema.Input<
  typeof redoCompetitiveSpreadSettingsForm
>;

export type RedoCompetitiveSpreadSettingsValue = InputSchema.Value<
  typeof redoCompetitiveSpreadSettingsForm
>;

export const competitiveSpreadSettingsDefault: CompetitiveSpreadSettingsValue =
  {
    enabled: true,
    beatRateBy: DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
    minBeatRateBy: DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
    carrierSpreadOverrides: DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
    minSpreadFloor: 0,
    enabledCarriers: [],
    applyMerchantCompSpreadWhenTied: false,
  };

export const redoCompetitiveSpreadSettingsDefault: RedoCompetitiveSpreadSettingsValue =
  {
    enabled: true,
    beatRateBy: DEFAULT_COMP_SPREAD_BEAT_RATE_BY,
    minBeatRateBy: DEFAULT_COMP_SPREAD_MIN_BEAT_RATE_BY,
    carrierSpreadOverrides: DEFAULT_COMP_SPREAD_CARRIER_OVERRIDES,
    minSpreadDifference: 0,
    enabledCarriers: [],
  };

type CarrierSpreadOverridesInput = InputSchema.Input<
  typeof carrierSpreadOverridesList
>;

function CarrierSpreadOverridesModal({
  isOpen,
  onClose,
  overridesInput,
  disabled,
}: {
  isOpen: boolean;
  onClose: () => void;
  overridesInput: CarrierSpreadOverridesInput;
  disabled: boolean;
}) {
  const carrierOptions = Object.values(Carriers).filter(
    (carrier) => !isUspsCarrier(carrier),
  );

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="lg"
      onModalCloseRequested={onClose}
      title="Carrier Spread Range Overrides"
    >
      <Flex dir="column" gap="lg">
        {overridesInput.inputs.map((override, index) => (
          <Flex
            align="flex-end"
            borderColor="secondary"
            borderStyle="solid"
            borderWidth="1px"
            dir="row"
            gap="md"
            key={index}
            p="md"
            radius="md"
          >
            <Flex dir="column" flex="1" gap="sm">
              <FormMultiSelectDropdown
                disabled={disabled}
                display={(value) => {
                  if (!value.length) {
                    return "Select carriers";
                  }
                  return value.join(", ");
                }}
                input={override.inputs.carriers}
                label="Carriers"
                options={carrierOptions}
              >
                {(option) => option}
              </FormMultiSelectDropdown>
            </Flex>
            <RedoIncrementDecrement
              disabled={disabled}
              label="Min beat rate by"
              min={0.01}
              placeholder="0.00"
              prefix="$"
              setValue={override.inputs.minBeatRateBy.setValue}
              step={0.01}
              value={override.inputs.minBeatRateBy.value}
            />
            <RedoIncrementDecrement
              disabled={disabled}
              label="Max beat rate by"
              min={0.01}
              placeholder="0.00"
              prefix="$"
              setValue={override.inputs.maxBeatRateBy.setValue}
              step={0.01}
              value={override.inputs.maxBeatRateBy.value}
            />
            <RedoButton
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => {
                const updated = [...overridesInput.value];
                updated.splice(index, 1);
                overridesInput.setValue(updated);
              }}
              size="sm"
              theme="destructive"
            />
          </Flex>
        ))}
        <RedoButton
          hierarchy="secondary"
          onClick={() => {
            overridesInput.setValue([
              ...overridesInput.value,
              { ...carrierSpreadOverrideDefault },
            ]);
          }}
          size="sm"
          text="Add carrier override"
        />
      </Flex>
    </RedoModal>
  );
}

export const CompetitiveSpreadSettingsCard = memo(
  function CompetitiveSpreadSettingsCard({
    input,
    canEditSpreadRanges,
  }: {
    input: OutboundLabelForm;
    canEditSpreadRanges: boolean;
  }) {
    const { competitiveSpreadSettings, redoCompetitiveSpreadSettings } =
      input.inputs;
    const merchantEnabled = competitiveSpreadSettings.inputs.enabled.value;
    const redoEnabled = redoCompetitiveSpreadSettings.inputs.enabled.value;

    const [merchantOverridesOpen, setMerchantOverridesOpen] = useState(false);
    const [redoOverridesOpen, setRedoOverridesOpen] = useState(false);

    // We cannot change USPS rates, so we don't show it in the dropdown
    const carrierOptions = Object.values(Carriers).filter(
      (carrier) => !isUspsCarrier(carrier),
    );

    return (
      <Card title="Competitive Spread Settings" w="full">
        <Flex dir="column" gap="md">
          {/* Merchant Competitive Spread Settings */}
          <Text fontSize="lg" fontWeight="semibold">
            Merchant Competitive Spread
          </Text>
          <FormSwitch
            input={competitiveSpreadSettings.inputs.enabled}
            label="Enable merchant competitive spread pricing"
          />
          <Text fontSize="sm" textColor="secondary">
            Default beat rate range. The adjusted rate will beat the
            competitor's rate by a value within this range. Carrier-specific
            overrides take precedence if configured.
          </Text>
          <Flex
            borderColor="secondary"
            borderStyle="solid"
            borderWidth="1px"
            dir="row"
            gap="md"
            justify="center"
            p="md"
            radius="md"
          >
            <RedoIncrementDecrement
              disabled={!merchantEnabled || !canEditSpreadRanges}
              label="Min beat rate by"
              min={0.01}
              placeholder="0.00"
              prefix="$"
              setValue={competitiveSpreadSettings.inputs.minBeatRateBy.setValue}
              step={0.01}
              value={competitiveSpreadSettings.inputs.minBeatRateBy.value}
            />
            <RedoIncrementDecrement
              disabled={!merchantEnabled || !canEditSpreadRanges}
              label="Max beat rate by"
              min={0.01}
              placeholder="0.00"
              prefix="$"
              setValue={competitiveSpreadSettings.inputs.beatRateBy.setValue}
              step={0.01}
              value={competitiveSpreadSettings.inputs.beatRateBy.value}
            />
          </Flex>
          <RedoButton
            disabled={!merchantEnabled || !canEditSpreadRanges}
            hierarchy="secondary"
            onClick={() => setMerchantOverridesOpen(true)}
            size="sm"
            text={`Manage carrier overrides (${competitiveSpreadSettings.inputs.carrierSpreadOverrides.value.length})`}
          />
          <CarrierSpreadOverridesModal
            disabled={!merchantEnabled || !canEditSpreadRanges}
            isOpen={merchantOverridesOpen}
            onClose={() => setMerchantOverridesOpen(false)}
            overridesInput={
              competitiveSpreadSettings.inputs.carrierSpreadOverrides
            }
          />

          {/* If this value needs to be changed, set it directly in the database.
          If the new value is going to be negative, make sure Suz, Aaron, or Jackie
          approves as it means we may potentially lose money on the labels.
          */}
          <RedoIncrementDecrement
            description="Please ask an engineer if you need to change this"
            disabled
            label="Minimum spread floor"
            placeholder="0.00"
            prefix="$"
            setValue={competitiveSpreadSettings.inputs.minSpreadFloor.setValue}
            step={0.01}
            value={competitiveSpreadSettings.inputs.minSpreadFloor.value}
          />
          <FormMultiSelectDropdown
            description="Select carriers where competitive spread should apply. If none are selected, all carriers will be included."
            disabled={!merchantEnabled}
            display={(value) => {
              if (!value.length) {
                return "No carriers selected";
              }
              if (value.length === carrierOptions.length) {
                return "All carriers";
              }
              return value.join(", ");
            }}
            input={competitiveSpreadSettings.inputs.enabledCarriers}
            label="Enabled carriers"
            options={carrierOptions}
          >
            {(option) => option}
          </FormMultiSelectDropdown>
          <FormSwitch
            disabled={!merchantEnabled}
            input={
              competitiveSpreadSettings.inputs.applyMerchantCompSpreadWhenTied
            }
            label="Apply competitive spread when the Redo rate is tied with merchant rate. We will lose a couple cents of spread when this is enabled, so only turn this on if the merchant complains that our rate is chosen by default when tied with theirs"
          />

          <Divider />

          {/* Redo Competitive Spread Settings */}
          <Text fontSize="lg" fontWeight="semibold">
            Redo Competitive Spread
          </Text>
          <FormSwitch
            input={redoCompetitiveSpreadSettings.inputs.enabled}
            label="Enable Redo competitive spread pricing"
          />
          <Text fontSize="sm" textColor="secondary">
            Default beat rate range. The adjusted rate will beat the competing
            Redo rate by a value within this range. Carrier-specific overrides
            take precedence if configured.
          </Text>
          <Flex
            borderColor="secondary"
            borderStyle="solid"
            borderWidth="1px"
            dir="row"
            gap="md"
            justify="center"
            p="md"
            radius="md"
          >
            <RedoIncrementDecrement
              disabled={!redoEnabled || !canEditSpreadRanges}
              label="Min beat rate by"
              min={0.01}
              placeholder="0.00"
              prefix="$"
              setValue={
                redoCompetitiveSpreadSettings.inputs.minBeatRateBy.setValue
              }
              step={0.01}
              value={redoCompetitiveSpreadSettings.inputs.minBeatRateBy.value}
            />
            <RedoIncrementDecrement
              disabled={!redoEnabled || !canEditSpreadRanges}
              label="Max beat rate by"
              min={0.01}
              placeholder="0.00"
              prefix="$"
              setValue={
                redoCompetitiveSpreadSettings.inputs.beatRateBy.setValue
              }
              step={0.01}
              value={redoCompetitiveSpreadSettings.inputs.beatRateBy.value}
            />
          </Flex>
          <RedoButton
            disabled={!redoEnabled || !canEditSpreadRanges}
            hierarchy="secondary"
            onClick={() => setRedoOverridesOpen(true)}
            size="sm"
            text={`Manage carrier overrides (${redoCompetitiveSpreadSettings.inputs.carrierSpreadOverrides.value.length})`}
          />
          <CarrierSpreadOverridesModal
            disabled={!redoEnabled || !canEditSpreadRanges}
            isOpen={redoOverridesOpen}
            onClose={() => setRedoOverridesOpen(false)}
            overridesInput={
              redoCompetitiveSpreadSettings.inputs.carrierSpreadOverrides
            }
          />

          <RedoIncrementDecrement
            description="Please ask an engineer if you need to change this"
            disabled
            label="Minimum spread difference"
            placeholder="0.00"
            prefix="$"
            setValue={
              redoCompetitiveSpreadSettings.inputs.minSpreadDifference.setValue
            }
            step={0.01}
            value={
              redoCompetitiveSpreadSettings.inputs.minSpreadDifference.value
            }
          />
          <FormMultiSelectDropdown
            description="Select carriers where competitive spread should apply. If none are selected, all carriers will be included."
            disabled={!redoEnabled}
            display={(value) => {
              if (!value.length) {
                return "No carriers selected";
              }
              if (value.length === carrierOptions.length) {
                return "All carriers";
              }
              return value.join(", ");
            }}
            input={redoCompetitiveSpreadSettings.inputs.enabledCarriers}
            label="Enabled carriers"
            options={carrierOptions}
          >
            {(option) => option}
          </FormMultiSelectDropdown>
        </Flex>
      </Card>
    );
  },
);
